import React from 'react';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import capitalizeFirst from 'utils/capitalizeFirst';
import applicationDetailSignal from '../../ApplicationDetail/ApplicationDetail.signal';
import { handleApplicationStatusDropdownChange } from './ApprovableStatusTab.helpers';
import userAccountSignal from 'signals/UserAccount.signal';
import ConfirmStatusModal from '../../ApplicationDetail/components/ApplicationConfirmStatusModal/ApplicationConfirmStatusModal';
import loaderSignal from 'signals/Loader.signal';
import { showConfirmStatusModal } from '../../ProgramRequestDetail/ProgramRequestDetail.helpers';
import {
  getFundingProgramTemplates,
  templateExists,
} from '../Approvable.helpers';
import $appSettings from 'signals/AppSettings.signal';
import { handleSendTechnicalAssistanceClick } from 'components/views/Edo/EdoTechnicalAssistance/edoTechnicalAssistance.helpers';

const ApprovableStatusTab = () => {
  const {
    id,
    newApplicationStatus,
    status,
    modelType,
    approvalChainTemplates = [],
    documents,
    fundingProgram,
    watchableId,
    ventureCapitalist,
    technicalAssistance,
  } = applicationDetailSignal.value;
  const { userData } = userAccountSignal.value;
  const { APPLICATION_STATUS, PORTAL_TYPES, DOCUMENT_TYPES } = $appSettings.value.constants;
  const { isContentLoading } = loaderSignal.value;
  const fundingProgramTemplates = getFundingProgramTemplates(
    fundingProgram,
    modelType === 'application'
      ? DOCUMENT_TYPES.applicationTemplate
      : DOCUMENT_TYPES.programRequestTemplate
  );

  const hasApplicationDocument = templateExists(
    documents,
    fundingProgramTemplates
  );

  const hasApprovalChain = (approvalChainTemplates?.length ?? 0) > 0;
  const isStatusDropdownDisabled =
    status === APPLICATION_STATUS?.denied ||
    (modelType === 'application' &&
      status === APPLICATION_STATUS?.inProgress) ||
    (modelType === 'application' &&
      status === APPLICATION_STATUS?.submittedForApproval &&
      hasApprovalChain) ||
    status === APPLICATION_STATUS?.approved;

  const entityLabel = modelType === 'application' ? 'application' : 'program request';
  const statusDropdownDisabledReason = isStatusDropdownDisabled
    ? status === APPLICATION_STATUS?.denied
      ? `Status cannot be changed because this ${entityLabel} has been denied.`
      : status === APPLICATION_STATUS?.approved
        ? `Status cannot be changed because this ${entityLabel} has already been approved.`
        : modelType === 'application' &&
            status === APPLICATION_STATUS?.inProgress
          ? 'Status cannot be changed on a Draft. The application must be submitted and Pending before you can update status here.'
          : modelType === 'application' &&
            status === APPLICATION_STATUS?.submittedForApproval &&
            hasApprovalChain
          ? 'Status cannot be changed while the application is submitted for approval.'
          : null
    : null;

  return (
    <>
      <Row className="mt-16">
        <Col>
          <h5>
            Current Status:{' '}
            {(status && status === APPLICATION_STATUS?.inProgress
              ? 'Draft'
              : capitalizeFirst(status).replace(/_/g, ' ')) || ''}{' '}
            {watchableId ? '(watching)' : ''}
          </h5>
          {status &&
            status === APPLICATION_STATUS?.denied &&
            userData.account.portalType === PORTAL_TYPES.edo && (
              <Button
                variant="outline-primary"
                onClick={() =>
                  handleSendTechnicalAssistanceClick(
                    id,
                    technicalAssistance?.id
                  )
                }
              >
                Send Technical Assistance
              </Button>
            )}
        </Col>
        <Col
          hidden={userData.account.portalType !== PORTAL_TYPES.edo}
        >
          <Alert
            variant="danger"
            hidden={
              !modelType ||
              modelType === 'programRequest' ||
              hasApplicationDocument
            }
          >
            An application document is missing!
          </Alert>
          <Form.Group className="form-group">
            <Form.Label className="font-weight-bold">Update Status</Form.Label>
            <br />
            <Form.Select
              disabled={isStatusDropdownDisabled}
              className="form-control"
              value={newApplicationStatus}
              name="newApplicationStatus"
              onChange={handleApplicationStatusDropdownChange}
            >
              <option value="">Select New Status</option>
              {modelType === 'programRequest' &&
                (ventureCapitalist ? (
                  status === APPLICATION_STATUS?.submittedForApproval ? (
                    <option value="APPROVED">Approved</option>
                  ) : (
                    <option value="SUBMITTED_FOR_APPROVAL">
                      Submit for Approval
                    </option>
                  )
                ) : (
                  <option value="APPROVED">Approved</option>
                ))}
              {modelType === 'application' &&
                (status === APPLICATION_STATUS?.submittedForApproval ? (
                  <option value={APPLICATION_STATUS?.approved}>Approved</option>
                ) : (
                  <option value="SUBMITTED_FOR_APPROVAL">
                    Submit for Approval
                  </option>
                ))}
              <option value="DENIED">Denied</option>
            </Form.Select>
            {statusDropdownDisabledReason && (
              <Form.Text className="text-muted d-block mt-1">
                {statusDropdownDisabledReason}
              </Form.Text>
            )}
          </Form.Group>
          <Row className="px-16 justify-content-end">
            <Button
              disabled={
                newApplicationStatus === '' ||
                status === APPLICATION_STATUS?.denied ||
                (modelType === 'application' &&
                  status === APPLICATION_STATUS?.inProgress) ||
                (modelType === 'application' &&
                  status === APPLICATION_STATUS?.submittedForApproval &&
                  newApplicationStatus !== APPLICATION_STATUS?.approved) ||
                status === APPLICATION_STATUS?.approved ||
                isContentLoading
              }
              className="rounded"
              onClick={() => {
                if (modelType === 'application') {
                  applicationDetailSignal.update({
                    confirmStatusVisible: true,
                  });
                } else {
                  showConfirmStatusModal();
                }
              }}
            >
              Submit
            </Button>
          </Row>
        </Col>
      </Row>
      <ConfirmStatusModal />
    </>
  );
};

export default ApprovableStatusTab;
